import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Loader2, Undo2, Check } from 'lucide-react';
import { Input } from './ui/input';
import { foodPreferencesAPI } from '../utils/api';
import { itemKey } from '../utils/foodPreferencesDraft';

// One SEARCH-LED picker for Settings and onboarding. Every selectable result
// comes from the backend (GET /food-preferences/search: groups + exact-food
// concepts) and is stored as its canonical { kind, id, label } — the label is
// only the wording the person saw. There is no free-text add: typed text only
// searches. The UI shows the food/group name and its selected state only: no
// default bubble cloud of groups and no Covers/Includes/example lists (the
// underlying Core Food variants a concept covers are never listed here).

var UNAVAILABLE_COPY = {
  exact_food_allergens_unavailable: "Exact-food allergens aren't available yet",
  concept_coverage_incomplete: 'Not verified across all recipes yet',
  not_selectable_for_purpose: 'Not available here',
  not_available: 'Not available',
  deprecated: 'No longer available',
};

function statusNote(item, purpose) {
  if (item.kind !== 'concept') return null;
  if (item.status === 'not_enforced' && purpose === 'allergen') return 'Not checked in meal planning yet';
  if (item.status === 'coverage_incomplete' && purpose === 'allergen') return "Some recipes can't be verified yet and are skipped";
  if (item.status === 'deprecated') return 'No longer available';
  if (item.status === 'missing') return 'No longer available';
  return null;
}

export default function FoodPreferencePicker(props) {
  var purpose = props.purpose; // 'allergen' | 'dislike'
  var list = purpose === 'allergen' ? 'allergens' : 'dislikes';
  var items = props.items || [];
  var unresolved = props.unresolved || [];
  var baselineKeys = props.baselineKeys || null; // Settings: saved keys; onboarding: null
  var removedItems = props.removedItems || [];
  var disabled = !!props.disabled;
  // props.groups (the options payload) is still accepted for compatibility but
  // no longer rendered: groups are found through search like everything else.
  var prefix = props.testIdPrefix || list;
  var searchFn = props.searchFn || foodPreferencesAPI.search;

  var [query, setQuery] = useState('');
  var [results, setResults] = useState([]);
  var [searching, setSearching] = useState(false);
  var [noMatch, setNoMatch] = useState(false);
  var latest = useRef(0);

  useEffect(function () {
    var q = query.trim();
    if (q.replace(/\s/g, '').length < 2) {
      setResults([]);
      setNoMatch(false);
      setSearching(false);
      return undefined;
    }
    var ticket = ++latest.current;
    setSearching(true);
    var timer = setTimeout(function () {
      Promise.resolve(searchFn(q, purpose))
        .then(function (resp) {
          if (ticket !== latest.current) return;
          var data = (resp && resp.data) || {};
          setResults(data.results || []);
          setNoMatch(!!data.no_match);
        })
        .catch(function () {
          if (ticket !== latest.current) return;
          setResults([]);
          setNoMatch(false);
        })
        .finally(function () {
          if (ticket === latest.current) setSearching(false);
        });
    }, props.debounceMs == null ? 250 : props.debounceMs);
    return function () { clearTimeout(timer); };
  }, [query, purpose]); // eslint-disable-line react-hooks/exhaustive-deps

  var selectedKeys = {};
  items.forEach(function (i) { selectedKeys[itemKey(i)] = true; });

  function choose(result) {
    if (disabled || !result.selectable) return;
    props.onAdd({ kind: result.kind, id: result.id, label: result.display_name });
    setQuery('');
    setResults([]);
    setNoMatch(false);
  }

  function onKeyDown(e) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    var first = results.find(function (r) { return r.selectable && !selectedKeys[r.kind + ':' + r.id]; });
    if (first) choose(first); // never adds typed text on its own
  }

  return (
    <div className="space-y-3" data-testid={prefix + '-picker'}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          value={query}
          disabled={disabled}
          onChange={function (e) { setQuery(e.target.value); }}
          onKeyDown={onKeyDown}
          placeholder={purpose === 'allergen' ? 'Search allergens' : 'Search foods you dislike'}
          data-testid={prefix + '-search'}
          className="pl-10 h-11 rounded-xl"
        />
        {searching && (
          <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {query.trim().length >= 2 && (results.length > 0 || noMatch) && (
        <div className="bg-card border border-border/40 rounded-xl shadow-sm divide-y divide-border/30"
          data-testid={prefix + '-results'}>
          {results.map(function (r) {
            var already = selectedKeys[r.kind + ':' + r.id];
            var unavailable = !r.selectable;
            return (
              <button
                key={r.kind + ':' + r.id}
                type="button"
                disabled={disabled || unavailable || already}
                onClick={function () { choose(r); }}
                data-testid={prefix + '-result-' + r.kind + '-' + r.id}
                data-selected={already ? 'true' : 'false'}
                className={'w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ' +
                  (already ? 'cursor-default' : unavailable ? 'opacity-60 cursor-not-allowed' : 'hover:bg-secondary/50')}
              >
                <span className="flex-1 min-w-0">
                  <span className={'font-medium ' + (already ? 'text-accent' : '')}>{r.display_name}</span>
                  {unavailable && !already && (
                    <span className="block text-xs text-muted-foreground">
                      {UNAVAILABLE_COPY[r.unavailable_reason] || 'Not available'}
                    </span>
                  )}
                </span>
                {already && (
                  <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-accent">
                    <Check size={14} /> Selected
                  </span>
                )}
              </button>
            );
          })}
          {noMatch && (
            <p className="px-4 py-2.5 text-sm text-muted-foreground" data-testid={prefix + '-no-match'}>
              No matching food or group.
            </p>
          )}
        </div>
      )}

      {(items.length > 0 || unresolved.length > 0 || removedItems.length > 0) && (
        <div className="flex flex-wrap gap-2" data-testid={prefix + '-selected'}>
          {items.map(function (item) {
            var pending = baselineKeys && !baselineKeys[itemKey(item)];
            var status = statusNote(item, purpose);
            return (
              <button
                key={itemKey(item)}
                type="button"
                disabled={disabled}
                onClick={function () { props.onRemove(itemKey(item)); }}
                data-testid={prefix + '-pill-' + item.kind + '-' + item.id}
                data-pending={pending ? 'true' : 'false'}
                className={'px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 border-2 ' +
                  (pending ? 'border-dashed border-amber-500 bg-amber-500/10 text-foreground'
                    : 'border-accent bg-accent/10 text-accent')}
              >
                {item.label}
                {pending && <span className="text-[10px] uppercase tracking-wide text-amber-600">Not saved</span>}
                {status && <span className="text-[10px] text-muted-foreground">({status})</span>}
                <X size={14} />
              </button>
            );
          })}
          {unresolved.map(function (u) {
            return (
              <button
                key={'unresolved:' + u.raw}
                type="button"
                disabled={disabled}
                onClick={function () { props.onRemoveUnresolved(u.raw); }}
                data-testid={prefix + '-unresolved-' + u.raw}
                title="We don't recognize this item, so it can't be checked reliably. Tap to remove."
                className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 border-2 border-border bg-secondary/30 text-muted-foreground"
              >
                {u.label || u.raw}
                <span className="text-[10px] uppercase tracking-wide">Unrecognized</span>
                <X size={14} />
              </button>
            );
          })}
          {removedItems.map(function (item) {
            return (
              <button
                key={'removed:' + itemKey(item)}
                type="button"
                disabled={disabled}
                onClick={function () { props.onUndoRemove(item); }}
                data-testid={prefix + '-removed-' + item.kind + '-' + item.id}
                className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 border-2 border-dashed border-border text-muted-foreground line-through"
              >
                {item.label}
                <span className="no-underline text-[10px] uppercase tracking-wide">Undo</span>
                <Undo2 size={14} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
